/**
 * Backup Schedule API client (Spec 201 — US2)
 *
 * Graphical schedule definitions + preview (summary/next-runs/compiled) + templates.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import apiClient from '../services/api';

export type BackupType = 'full' | 'incremental';
export type Frequency = 'hourly' | 'daily' | 'weekly' | 'monthly';

export interface ScheduleRule {
  backupType: BackupType;
  frequency: Frequency;
  time?: string;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  intervalHours?: number;
}

export interface ScheduleBuilder {
  timezone: string;
  rules: ScheduleRule[];
}

export interface SchedulePreview {
  valid: boolean;
  errors: string[];
  summary?: string;
  compiled?: Array<{ cronExpression: string; backupType: BackupType }>;
  nextRuns?: Array<{ time: string; backupType: BackupType }>;
  // Spec 214 T065: first upcoming run PER cadence (a weekly full never surfaced in next-5).
  nextPerType?: Array<{ time: string; backupType: BackupType }>;
}

export interface ScheduleTemplate {
  key: string;
  label: string;
  builder: ScheduleBuilder;
}

export interface ScheduleDefinition {
  id: string;
  name: string;
  frequency: string;
  builderJson: string;
  timezone: string;
  isAdvanced: boolean;
  createdAt: string;
  updatedAt: string;
  summary?: string; // plain-language cadence (from the list endpoint)
  usedByPlans?: number; // # of ACTIVE plans referencing it (0 = safe to delete — Spec 214 R13b)
  usedByArchivedPlans?: number; // archived references, display-only (never blocks delete)
}

/** Weekly schedule-load heatmap: cells[dayOfWeek 0..6 (Sun..Sat)][hour 0..23] = # backups starting
 *  that hour (volume); peaks[d][h] = the most that start in any single minute (the cap-relevant
 *  concurrency — a 5-minute-staggered schedule has a high hourly count but a low peak). */
export interface ScheduleSpread {
  cells: number[][];
  peaks?: number[][];
  cap: number;
  max: number;
  total: number;
  // Spec 214 FR-040: monthly cadences in a dedicated row (day-of-month honored) + one labeled tz.
  monthly?: number[];
  timezone?: string;
}

type Wrapped<T> = { success: boolean; data: T };
const BASE = '/api/v1/backup/schedule-definitions';

export const schedulesApi = {
  list: async (): Promise<ScheduleDefinition[]> => (await apiClient.get<Wrapped<ScheduleDefinition[]>>(BASE)).data,

  spread: async (): Promise<ScheduleSpread> => (await apiClient.get<Wrapped<ScheduleSpread>>(`${BASE}/spread`)).data,

  templates: async (): Promise<ScheduleTemplate[]> =>
    (await apiClient.get<Wrapped<ScheduleTemplate[]>>(`${BASE}/templates`)).data,

  preview: async (builder: ScheduleBuilder): Promise<SchedulePreview> =>
    (await apiClient.post<Wrapped<SchedulePreview>>(`${BASE}/preview`, builder)).data,

  create: async (name: string, builder: ScheduleBuilder, isAdvanced = false): Promise<ScheduleDefinition> =>
    (await apiClient.post<Wrapped<ScheduleDefinition>>(BASE, { name, builder, isAdvanced })).data,

  update: async (id: string, patch: { name?: string; builder?: ScheduleBuilder }): Promise<ScheduleDefinition> =>
    (await apiClient.put<Wrapped<ScheduleDefinition>>(`${BASE}/${id}`, patch)).data,

  remove: async (id: string): Promise<{ deleted: boolean }> =>
    (await apiClient.delete<Wrapped<{ deleted: boolean }>>(`${BASE}/${id}`)).data,
};
